import { isString } from 'ramda-adjunct';
import { useHandleLink } from '../hooks/useHandleLink';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ActionCallback } from '../types/ActionCallback';
import type { RowData } from '../types/RowData';
import { getColumnData } from '../utils/getColumnData';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSourceSearchParamsByGridCode } from '../utils/url/setSourceSearchParamsByGridCode';
type HandleActionClick = (actionKey: string, row: RowData, event: React.MouseEvent) => void;

/**
 * Creates a stable callback for handling row action clicks with per-action locking.
 *
 * Instantiate ONCE in EagGridTable — NOT per-row.
 * The `row` argument is passed per-click, not bound in the hook.
 *
 * Locking behavior:
 * - If callback returns a Promise → auto-lock with reason 'promise', auto-unlock on settle.
 * - If callback calls actionLock.lock() → manual lock with reason 'manual', must unlock manually.
 * - Both can coexist: action stays disabled until ALL locks are released.
 * - Clicks on already-locked actions are silently ignored (double-click protection).
 */
export function useRowActionClick(actionCallback: ActionCallback<readonly string[]> | undefined, gridCode: string, queryIdRef: React.RefObject<string>, serverDataRefreshFn: () => void): HandleActionClick {
  const store = useEagGridStore();
  const handleLink = useHandleLink();
  return (actionKey: string, row: RowData, event: React.MouseEvent) => {
    if (!actionCallback) {
      return;
    }

    // Double-click protection: ignore if this action is already locked
    if (store.isActionLocked(row.id, actionKey)) {
      return;
    }
    const rowId = row.id;
    const sourceSearchParams = setSourceSearchParamsByGridCode(gridCode, String(row.rowNumber));
    const searchParams = getSearchParamsByGridCode(gridCode);

    // Manual lock/unlock control — passed to the callback
    const actionLock = {
      lock: () => store.lockAction(rowId, actionKey, 'manual'),
      unlock: () => store.unlockAction(rowId, actionKey, 'manual')
    };
    const result = actionCallback({
      actionKey,
      rowId,
      getColumnData: getColumnData(row, gridCode),
      rowData: row,
      refreshData: serverDataRefreshFn,
      refreshCells: () => undefined,
      redrawRows: () => undefined,
      deselectAll: () => store.deselectAll(),
      sourceSearchParams,
      queryId: searchParams?.queryId ?? queryIdRef.current ?? '',
      actionLock
    });

    // Check for thenable (Promise-like) — without instanceof (cross-realm safe)
    if (result !== null && result !== undefined && typeof (result as Promise<unknown>).then === 'function') {
      // Auto-lock for the duration of the Promise
      store.lockAction(rowId, actionKey, 'promise');
      (result as Promise<string | void>).then(actionLink => {
        if (isString(actionLink)) {
          handleLink(actionLink, event.nativeEvent as MouseEvent);
        }
      }).catch(() => {
        // Rejected: consumer is responsible for error display (toast, etc.)
        // We only clean up the lock.
      }).finally(() => {
        // Remove ONLY the promise lock — manual lock is independent
        store.unlockAction(rowId, actionKey, 'promise');
      });
    } else if (isString(result)) {
      // Synchronous link navigation
      handleLink(result, event.nativeEvent as MouseEvent);
    }
    // void return → synchronous side-effect, no locking
  };
}